/**
 * 矩阵化坐标背景：
 * - 平面直角坐标系网格（24px 细网格 / 120px 主网格 + 中央轴与刻度）；
 * - 网格交点填点阵；点半径/亮度随鼠标距离衰减（近大远小），形成光学透镜感；
 * - 纯 canvas，DPR 自适应，鼠标离开后回到基准状态。
 *
 * 设计目标：极简制图台，而不是“科技霓虹背景”。所有颜色保持冷白/冰青低饱和。
 */
import { useEffect, useRef } from 'react';
import { useSettings } from '../../state/settings.ts';

export function MatrixBackdrop() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const settings = useSettings();
  const { backgroundDots, backgroundIntensity } = settings;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let width = 0;
    let height = 0;
    let dpr = 1;
    let frame = 0;
    const pointer = { x: -9999, y: -9999 };

    const resize = () => {
      dpr = Math.min(2, window.devicePixelRatio || 1);
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const draw = () => {
      frame = 0;
      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = '#040507';
      ctx.fillRect(0, 0, width, height);

      const cx = width / 2;
      const cy = height / 2;
      const minor = 24;
      const major = 120;

      // 细网格
      ctx.lineWidth = 0.6;
      ctx.strokeStyle = 'rgba(255,255,255,0.042)';
      ctx.beginPath();
      for (let x = cx % minor; x < width; x += minor) {
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
      }
      for (let y = cy % minor; y < height; y += minor) {
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
      }
      ctx.stroke();

      // 主网格
      ctx.lineWidth = 1;
      ctx.strokeStyle = 'rgba(255,255,255,0.085)';
      ctx.beginPath();
      for (let x = cx % major; x < width; x += major) {
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
      }
      for (let y = cy % major; y < height; y += major) {
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
      }
      ctx.stroke();

      // 内容布局参考线：以 120px 为单位、最大 1440px 的 12 列画布
      ctx.lineWidth = 0.75;
      ctx.strokeStyle = 'rgba(186,230,253,0.032)';
      ctx.beginPath();
      for (let column = -6; column <= 6; column += 1) {
        const x = cx + column * major;
        if (x < 0 || x > width) continue;
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
      }
      ctx.stroke();

      // 中央坐标轴
      ctx.strokeStyle = 'rgba(186,230,253,0.2)';
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.moveTo(cx, 0);
      ctx.lineTo(cx, height);
      ctx.moveTo(0, cy);
      ctx.lineTo(width, cy);
      ctx.stroke();

      // 主网格刻度
      ctx.strokeStyle = 'rgba(255,255,255,0.2)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (let x = cx % major; x < width; x += major) {
        ctx.moveTo(x, cy - 5);
        ctx.lineTo(x, cy + 5);
      }
      for (let y = cy % major; y < height; y += major) {
        ctx.moveTo(cx - 5, y);
        ctx.lineTo(cx + 5, y);
      }
      ctx.stroke();

      // 边缘制图框 + 角标 + 刻度尺
      const inset = 20;
      ctx.strokeStyle = 'rgba(255,255,255,0.11)';
      ctx.lineWidth = 1;
      ctx.strokeRect(inset, inset, width - inset * 2, height - inset * 2);
      ctx.strokeStyle = 'rgba(186,230,253,0.22)';
      ctx.beginPath();
      for (const [x, y, dx, dy] of [
        [inset, inset, 18, 0],
        [inset, inset, 0, 18],
        [width - inset, inset, -18, 0],
        [width - inset, inset, 0, 18],
        [inset, height - inset, 18, 0],
        [inset, height - inset, 0, -18],
        [width - inset, height - inset, -18, 0],
        [width - inset, height - inset, 0, -18],
      ] as const) {
        ctx.moveTo(x, y);
        ctx.lineTo(x + dx, y + dy);
      }
      ctx.stroke();

      ctx.strokeStyle = 'rgba(255,255,255,0.14)';
      ctx.beginPath();
      for (let x = inset; x < width - inset; x += minor) {
        const long = (x - cx) % major === 0;
        ctx.moveTo(x, inset);
        ctx.lineTo(x, inset + (long ? 8 : 3));
        ctx.moveTo(x, height - inset);
        ctx.lineTo(x, height - inset - (long ? 8 : 3));
      }
      for (let y = inset; y < height - inset; y += minor) {
        const long = (y - cy) % major === 0;
        ctx.moveTo(inset, y);
        ctx.lineTo(inset + (long ? 8 : 3), y);
        ctx.moveTo(width - inset, y);
        ctx.lineTo(width - inset - (long ? 8 : 3), y);
      }
      ctx.stroke();

      // 坐标读数：轻量，不干扰内容
      ctx.font = '9px "JetBrains Mono", ui-monospace, monospace';
      ctx.fillStyle = 'rgba(186,230,253,0.25)';
      ctx.textAlign = 'center';
      for (let column = -5; column <= 5; column += 1) {
        if (column === 0) continue;
        const x = cx + column * major;
        if (x > inset + 20 && x < width - inset - 20) ctx.fillText(String(column), x, cy + 18);
      }
      ctx.textAlign = 'right';
      for (let row = -3; row <= 3; row += 1) {
        if (row === 0) continue;
        const y = cy + row * major;
        if (y > inset + 20 && y < height - inset - 20) ctx.fillText(String(-row), cx - 10, y + 3);
      }
      ctx.textAlign = 'left';
      ctx.fillText('GRID 24 / MATRIX CANVAS', inset + 10, inset + 16);

      // 点阵：距离鼠标越近，点越大越亮；整页铺满，作为画布底层
      if (backgroundDots) {
        const spacing = 24;
        const intensity = Math.max(0.35, Math.min(1.8, backgroundIntensity));
        if (pointer.x > -1000) {
          const lens = ctx.createRadialGradient(pointer.x, pointer.y, 0, pointer.x, pointer.y, 190);
          lens.addColorStop(0, 'rgba(186,230,253,0.035)');
          lens.addColorStop(1, 'rgba(186,230,253,0)');
          ctx.fillStyle = lens;
          ctx.fillRect(pointer.x - 190, pointer.y - 190, 380, 380);
        }
        for (let x = cx % spacing; x < width; x += spacing) {
          for (let y = cy % spacing; y < height; y += spacing) {
            const dx = x - pointer.x;
            const dy = y - pointer.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            const influence = Math.exp(-distance / 165);
            const radius = 1.05 + influence * 3.1;
            const alpha = Math.min(0.9, (0.13 + influence * 0.62) * intensity);
            ctx.beginPath();
            ctx.fillStyle = `rgba(225,238,250,${alpha.toFixed(3)})`;
            ctx.arc(x, y, radius, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      }

      // 原点准星
      ctx.strokeStyle = 'rgba(255,255,255,0.22)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(cx, cy, 3.5, 0, Math.PI * 2);
      ctx.moveTo(cx - 8, cy);
      ctx.lineTo(cx + 8, cy);
      ctx.moveTo(cx, cy - 8);
      ctx.lineTo(cx, cy + 8);
      ctx.stroke();
    };

    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(draw);
    };

    const onPointerMove = (event: PointerEvent) => {
      pointer.x = event.clientX;
      pointer.y = event.clientY;
      schedule();
    };
    const onPointerLeave = () => {
      pointer.x = -9999;
      pointer.y = -9999;
      schedule();
    };
    const onResize = () => {
      resize();
      schedule();
    };

    resize();
    schedule();
    window.addEventListener('resize', onResize, { passive: true });
    window.addEventListener('pointermove', onPointerMove, { passive: true });
    window.addEventListener('pointerleave', onPointerLeave, { passive: true });
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerleave', onPointerLeave);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [backgroundDots, backgroundIntensity]);

  return (
    <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden" aria-hidden="true" data-testid="matrix-backdrop">
      <canvas ref={canvasRef} className="absolute inset-0" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_45%,transparent_20%,rgba(4,5,7,0.28)_78%,rgba(4,5,7,0.72)_100%)]" />
      <div className="absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-[#040507]/75 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#040507] to-transparent" />
    </div>
  );
}

